<template>
<div>
    <Breadcrumbs main="Admin" submain="Subscription" title="User" />
    <!-- Container-fluid starts-->
    <div class="container-fluid">
        <div class="row">
            <div class="col-md-12">
                <div class="card">
                    <div class="card-body">
                        <b-row class="d-flex justify-content-center align-items-center">
                            <b-col xl="6" class="d-flex flex-wrap" style="gap: 20px">
                                <b-input-group style="width: 340px;">
                                    <b-form-input v-model="user_name" @keyup="searchUsers" placeholder="Type to Search"></b-form-input>
                                    <b-input-group-append>
                                        <b-button @click="clearSearch">Clear</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-col>

                            <b-col xl="6">
                                <b-form-group label-cols="2" label="Per page" class="mb-0 datatable-select">
                                    <b-form-select v-model="perPage" :options="pageOptions"></b-form-select>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <div v-if="!loading && !error && !loadingPage">
                            <div class="table-responsive datatable-vue mt-4">
                                <b-table :items="subscriber_list" :fields="tablefields" :per-page="perPage" show-empty stacked="md">
                                    <template #cell(index)="data">
                                        {{ (currentPage - 1) * perPage + data.index + 1 }}
                                    </template>

                                    <template #cell(user_object)="row">
                                        <div style="display: flex; flex-direction: column; gap: 4px;">
                                            <h7>{{ row.item.user_object?.first_name }} {{ row.item.user_object?.last_name }}</h7>
                                            <b-badge style="font-size: 13px; max-width: fit-content;">{{ row.item.user_object?.email }}</b-badge>
                                        </div>
                                    </template>

                                    <template #cell(profile_id)="row">
                                        <h7>{{ row.item.personal_info?.profile_id }}</h7>
                                    </template>

                                    <template #cell(phone_no)="row">
                                        <h7>{{ row.item.personal_info?.phone_number }}</h7>
                                    </template>

                                    <template #cell(utm)="row">
                                        <div v-if="getUtm(row.item.personal_info).length" style="display: flex; flex-direction: column; gap: 4px;">
                                            <span v-for="utm in getUtm(row.item.personal_info)" :key="utm.key" style="white-space: nowrap;">
                                                <span style="font-weight: 600;">{{ utm.label }}:</span> {{ utm.value }}
                                            </span>
                                        </div>
                                        <h7 v-else>N/A</h7>
                                    </template>

                                    <template #cell(completion)="row">
                                        <h7>{{ row.item?.completion }}%</h7>
                                    </template>

                                    <template #cell(personal_info.first_registration_date)="row">
                                        <h7 v-if="row.item.personal_info">{{ formatDate(row.item.personal_info?.first_registration_date).date }} <br> {{ formatDate(row.item.personal_info?.first_registration_date).time }}</h7>
                                    </template>

                                    <template #cell(wallet_balance)="row">
                                        <router-link :to="{ path: '/subscription/user/wallet', query: { user_id: row.item.user_object?.id } }">
                                            <b-badge variant="primary" style="font-size: small;">{{ row.item.personal_info?.wallet_balance }}</b-badge>
                                            <p style="padding-top: 8px; font-weight: 600; border-bottom: 2px solid #262062; width: 54px;">History</p>
                                        </router-link>
                                    </template>

                                    <template #cell(action)="data">
                                        <div style="display: flex; gap: 10px; align-items: center;">
                                            <input style="width: 16px; height: 16px; cursor: pointer;" type="checkbox" id="flexSwitchCheck" v-model="data.item.personal_info.test_property" @change="handleToggle(data.item.personal_info?.user)" v-b-tooltip.hover title="Toggle Test Profile" />
                                            <router-link :to="{ path: '/subscription/user/activity-log', query: { user_id: data.item?.user_object?.id } }">
                                                <feather type="eye" size="21px" style="color: #262261; cursor: pointer; margin-top: 6px;" v-b-tooltip.hover title="Activity Log"></feather>
                                            </router-link>
                                            <feather type="delete" size="20px" style="color: #ff595d; cursor: pointer;" v-b-tooltip.hover v-b-modal.modal-center @click="deleteUser(data.item?.user_object?.id)" title="Delete"></feather>
                                        </div>
                                    </template>
                                </b-table>
                            </div>
                        </div>

                        <div v-else>
                            <loader />
                        </div>

                        <b-row class="mt-3">
                            <b-col xl="12" class="d-flex flex-wrap justify-content-start align-items-center" style="gap: 8px;">
                                <span>
                                    <!-- First Page Button (<<) -->
                                    <button class="pg-button" :disabled="currentPage === 1" @click="goToPage(1)">
                                        <b-icon icon="chevron-double-left"></b-icon>
                                    </button>
    
                                    <!-- Previous Page Button (<) -->
    
                                    <button class="pg-button ml-2" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">
                                        <b-icon icon="chevron-left"></b-icon>
                                    </button>
                                </span>

                                <!-- Page Number Display (Page X of Y) -->
                                <span class="align-self-center">
                                    <span class="align-self-center">
                                        <span class="d-flex align-items-center" style="gap: 6px;">
                                            <span>Page</span>
                                            <b-form-input v-model="currentPage" style="box-shadow: none; width: 54px; text-align: center;" @blur="validatePage" @keydown.enter="validatePage" :disabled="totalPages <= 1"></b-form-input>
                                            <span>of {{ totalPages }}</span>
                                        </span>
                                    </span>
                                </span>

                                <span>
                                    <!-- Next Page Button (>) -->
                                    <button class="mr-2 pg-button" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)">
                                        <b-icon icon="chevron-right"></b-icon>
                                    </button>
    
                                    <!-- Last Page Button (>>) -->
                                    <button class="pg-button" :disabled="currentPage === totalPages" @click="goToPage(totalPages)">
                                        <b-icon icon="chevron-double-right"></b-icon>
                                    </button>
                                </span>
                            </b-col>
                        </b-row>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- Container-fluid Ends-->

    <!-- User Delete Modal -->
    <b-modal id="modal-center" centered hide-header @ok="handleDeleteOk">
        <div class="mt-4 mb-4" style="font-size: large;">Are you sure you want to delete this user?</div>
    </b-modal>
</div>
</template>

<script>
import { mapState } from 'vuex';
import { myHost } from '../../../_helpers/host';
import loader from '../loader/loader.vue';
import Multiselect from 'vue-multiselect';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';

export default {
    components: {
        loader,
        Multiselect
    },

    data() {
        return {
            host: myHost.host,
            selected: [],
            isDisabled: false,
            subscriber: {

            },
            tablefields: [
                {
                    key: 'index',
                    label: '#'
                },
                {
                    key: 'user_object',
                    label: 'Subscriber',
                    sortable: false
                },
                {
                    key: 'phone_no',
                    label: 'Phone No',
                    sortable: false
                },
                {
                    key: 'profile_id',
                    label: 'Profile ID',
                    sortable: false
                },
                {
                    key: 'personal_info.first_registration_date',
                    label: 'Registered On',
                    sortable: true
                },
                {
                    key: 'utm',
                    label: 'UTM',
                    sortable: false
                },
                {
                    key: 'completion',
                    label: 'Profile Completion',
                    sortable: false
                },
                {
                    key: 'wallet_balance',
                    label: 'Balance (Coin)',
                    sortable: false
                },
                {
                    key: 'action',
                    label: 'Action',
                    sortable: false
                }
            ],
            delete_value: null,
            initialLoad: true,
            filterShow: false,
            user_name: null,
            totalRows: 1,
            currentPage: 1,
            perPage: 25,
            loadingPage: false,
            pageOptions: [25, 50, 100]
        };
    },

    computed: {
        ...mapState({
            subscription_types: state => state.packages.package,
            subscriber_list: state => state.subscriber.subscriber,
            loading: state => state.subscriber.subscriberLoading,
            error: state => state.subscriber.subscriberError,
            count: state => state.subscriber.subscriber_count
        }),

        formattedSubscription() {
            return [{
                    value: null,
                    text: 'Select a subscription type',
                    disabled: true
                },
                ...this.subscription_types.map(type => ({
                    value: type.id,
                    text: type.title + " (" + type.type + ")"
                }))
            ];
        },

        totalPages() {
            if (typeof this.totalRows === 'number' && !isNaN(this.totalRows) && this.totalRows > 0) {
                return Math.ceil(this.totalRows / this.perPage);
            }
            return 1; // Default to 1 if totalRows is invalid
        }
    },

    watch: {
        currentPage(newCurrentPage) {
            if (!this.initialLoad) {
                if (this.currentPage === null || this.currentPage === '') {
                    this.loadingPage = true
                }

                this.$router.replace({
                    path: '/subscription/user/list',
                    query: {
                        page: newCurrentPage,
                        perPage: this.perPage
                    }
                });
                
                this.currentPage = newCurrentPage
                this.$store.dispatch("subscriber/fetchSubscriber", {
                    perPage: this.perPage,
                    currentPage: newCurrentPage,
                    user_name: this.user_name
                }).then(() => {
                    this.totalRows = this.count;
                    this.initialLoad = false
                });
            }
        },

        perPage(newperPage) {
            if (!this.initialLoad) {

               this.$router.replace({
                    path: '/subscription/user/list',
                    query: {
                        page: newCurrentPage,
                        perPage: this.perPage
                    }
                });

                this.perPage = newperPage
                this.$store.dispatch("subscriber/fetchSubscriber", {
                    perPage: newperPage,
                    currentPage: this.currentPage,
                    user_name: this.user_name
                }).then(() => {
                    this.totalRows = this.count;
                    this.initialLoad = false
                });
            }
        }
    },

    created() {
        this.$store.dispatch("packages/fetchPackage")
    },

    mounted() {
        if (this.initialLoad) {
            if (!this.$route.query.page || !this.$route.query.perPage) {
                this.$router.replace({
                    path: this.$route.path,
                    query: {
                        page: this.currentPage,
                        perPage: this.perPage
                    }
                }).catch(err => {
                    if (err.name !== 'NavigationDuplicated') {
                        throw err;
                    }
                });
                this.fetchInitial();
            } else {
                this.currentPage = parseInt(this.$route.query.page);
                this.perPage = parseInt(this.$route.query.perPage);
                this.fetchInitial();
            }
        }
    },

    methods: {
        validatePage() {
            this.loadingPage = true;
            const totalPages = this.totalPages;
            
            if (!this.currentPage || isNaN(this.currentPage) || this.currentPage < 1) {
                this.currentPage = 1;
            } else if (Number(this.currentPage) > totalPages) {
                this.currentPage = totalPages; // Set to last valid page
            }

            this.loadingPage = false;
        },

        goToPage(page) {
            if (page < 1 || page > this.totalPages) return;
            this.currentPage = page;
        },

       formatDate(dateStr) {
            if (!dateStr) return ''

            // Split date and time manually
            const [datePart, timePart] = dateStr.replace('Z', '').split('T')

            const [year, month, day] = datePart.split('-')
            const [hourRaw, minute] = timePart.split(':')

            const hours = parseInt(hourRaw)

            const monthNames = [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ]

            const monthName = monthNames[parseInt(month) - 1]

            const ampm = hours >= 12 ? 'PM' : 'AM'
            const hour12 = hours % 12 || 12

            const getOrdinal = (n) => {
                const s = ['th', 'st', 'nd', 'rd']
                const v = n % 100
                return s[(v - 20) % 10] || s[v] || s[0]
            }

            return {
                date: `${parseInt(day)}${getOrdinal(parseInt(day))} ${monthName}, ${year}`,
                time: `${hour12}:${minute} ${ampm}`
            }
        },

        getUtm(info) {
            if (!info) return []

            const fields = [
                { key: 'utm_source', label: 'Source' },
                { key: 'utm_medium', label: 'Medium' },
                { key: 'utm_campaign', label: 'Campaign' },
                { key: 'utm_term', label: 'Term' },
                { key: 'utm_content', label: 'Content' }
            ]

            return fields
                .filter(field => info[field.key])
                .map(field => ({ ...field, value: info[field.key] }))
        },

        fetchInitial() {
            this.$store.dispatch("subscriber/fetchSubscriber", {
                perPage: this.$route.query.perPage,
                currentPage: this.$route.query.page,
                user_name: this.user_name
            }).then(() => {
                this.totalRows = this.count;
                this.initialLoad = false;
            });
        },

        deleteUser(id) {
            this.delete_value = id
        },

        handleDeleteOk() {
            this.$store.dispatch("subscriber/deleteSubscriber", {
                delete: this.delete_value,
                user_name: this.user_name,
                perPage: this.$route.query.perPage,
                currentPage: this.$route.query.page
            }).then(response => {
                if (response.code == 200) {
                    Swal.fire({
                        title: "Success!",
                        text: response.message,
                        icon: "success"
                    });
                    this.delete_value = null
                } else {
                    Swal.fire({
                        title: "Error!",
                        text: response.response,
                        icon: "error"
                    });
                }
            })
        },

        handleToggle(user_id) {
            this.$store.dispatch("subscriber/toggleTestProfile", {
                user_id: user_id,
                user_name: this.user_name,
                perPage: this.$route.query.perPage,
                currentPage: this.$route.query.page
            }).then(response => {
                if (response.code == 200) {
                    Swal.fire({
                        title: "Success!",
                        text: response.message,
                        icon: "success"
                    });
                } else {
                    Swal.fire({
                        title: "Error!",
                        text: response.response,
                        icon: "error"
                    });
                }
            })
        },

        searchUsers() {
            if (this.user_name && this.user_name.trim() !== '' && this.user_name.length >= 2) {
                this.$store.dispatch("subscriber/fetchSubscriber", {
                    user_name: this.user_name,
                    perPage: this.$route.query.perPage,
                    currentPage: this.$route.query.page
                }).then(() => {
                    this.totalRows = this.count;
                    this.initialLoad = false;
                });
            }
        },

        clearSearch() {
            this.user_name = null;
            this.fetchInitial();
        }
    }
};
</script>

<style scoped>
.table-danger {
    background-color: #f8d7da !important;
    color: #721c24 !important;
}
</style>
